import { useState } from "react";
import { Loader2 } from "lucide-react";
import {
  loginInventoryStaff,
  registerInventoryStaff,
  loadSavedInventoryStaffPhone,
  saveInventoryStaffPhone,
} from "@/lib/inventoryStaff";

const PINK = "#EE2677";

function SignupForm({ initialPhone, onComplete, onCancel }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState(initialPhone || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    const digits = phone.replace(/\D/g, "");
    if ((name || "").trim().length < 2) { setErr("Enter your full name"); return; }
    if (digits.length < 10) { setErr("Enter your 10-digit mobile number"); return; }
    setBusy(true);
    setErr("");
    try {
      const data = await registerInventoryStaff({ name: name.trim(), phone });
      saveInventoryStaffPhone(phone);
      onComplete?.(data);
    } catch (ex) {
      setErr(ex.message || String(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ti-auth">
      <style>{AUTH_CSS}</style>
      <div className="ti-auth-brand">
        <p className="ti-auth-kicker">Inventory app</p>
        <h1 className="ti-auth-name">The Table Top</h1>
      </div>
      <div className="ti-auth-card">
        <h2 className="ti-auth-title">New team member</h2>
        <p className="ti-auth-sub">Ek baar sign-up karo — aage sirf number chahiye.</p>
        <form onSubmit={submit}>
          <div className="ti-auth-field">
            <label htmlFor="ti-signup-name">Your name</label>
            <input id="ti-signup-name" type="text" autoComplete="name" placeholder="Full name"
              value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="ti-auth-field">
            <label htmlFor="ti-signup-phone">Mobile number</label>
            <input id="ti-signup-phone" type="tel" inputMode="tel" autoComplete="tel"
              placeholder="10-digit mobile" value={phone}
              onChange={(e) => setPhone(e.target.value)} required />
          </div>
          {err ? <p className="ti-auth-err">{err}</p> : null}
          <button type="submit" className="ti-auth-primary" disabled={busy}>
            {busy ? <Loader2 className="animate-spin" size={18} style={{ verticalAlign: "middle" }} /> : null}
            {busy ? " Registering…" : "Register & open app"}
          </button>
        </form>
        <button type="button" className="ti-auth-link" onClick={onCancel}>
          Already registered? Log in
        </button>
      </div>
    </div>
  );
}

const AUTH_CSS = `
  .ti-auth {
    min-height: 100dvh; background: #F5F2EA; color: #2D2D2D;
    padding: 12px 14px 28px; max-width: 480px; margin: 0 auto;
    font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  }
  .ti-auth * { box-sizing: border-box; }
  .ti-auth-brand { text-align: center; margin: 24px 0 28px; }
  .ti-auth-kicker {
    font-size: 11px; font-weight: 800; letter-spacing: 0.12em;
    text-transform: uppercase; color: rgba(45,45,45,0.45); margin: 0 0 4px;
  }
  .ti-auth-name { font-size: 28px; font-weight: 900; color: ${PINK}; margin: 0; }
  .ti-auth-card {
    background: #fff; border-radius: 20px; padding: 20px 18px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  }
  .ti-auth-title { font-size: 20px; font-weight: 800; margin: 0 0 6px; }
  .ti-auth-sub { font-size: 14px; color: rgba(45,45,45,0.65); margin: 0 0 16px; line-height: 1.45; }
  .ti-auth-field { margin-bottom: 12px; }
  .ti-auth-field label { display: block; font-size: 13px; font-weight: 700; margin-bottom: 6px; }
  .ti-auth-field input {
    width: 100%; border: 2px solid #E5E7EB; border-radius: 14px;
    padding: 14px; font-size: 18px; background: #fff;
  }
  .ti-auth-primary {
    width: 100%; border: none; border-radius: 16px; padding: 16px;
    font-size: 17px; font-weight: 800; color: #fff; background: ${PINK};
    cursor: pointer; margin-top: 14px;
  }
  .ti-auth-primary:disabled { opacity: 0.6; cursor: wait; }
  .ti-auth-link {
    width: 100%; margin-top: 12px; border: none; background: transparent;
    color: ${PINK}; font-size: 15px; font-weight: 800; cursor: pointer; padding: 10px;
  }
  .ti-auth-err { color: #B91C1C; font-size: 14px; font-weight: 600; margin-top: 10px; }
`;

export default function InventoryStaffAuthGate({ onComplete, initialMode = "login" }) {
  const [mode, setMode] = useState(initialMode);
  const [phone, setPhone] = useState(() => loadSavedInventoryStaffPhone());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const login = async (e) => {
    e.preventDefault();
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 10) { setErr("Enter your 10-digit mobile number"); return; }
    setBusy(true);
    setErr("");
    try {
      const data = await loginInventoryStaff(phone);
      saveInventoryStaffPhone(phone);
      onComplete?.(data);
    } catch (ex) {
      setErr(ex.message || String(ex));
    } finally {
      setBusy(false);
    }
  };

  if (mode === "signup") {
    return (
      <SignupForm
        initialPhone={phone}
        onComplete={onComplete}
        onCancel={() => { setMode("login"); setErr(""); }}
      />
    );
  }

  return (
    <div className="ti-auth">
      <style>{AUTH_CSS}</style>
      <div className="ti-auth-brand">
        <p className="ti-auth-kicker">Inventory app</p>
        <h1 className="ti-auth-name">The Table Top</h1>
      </div>
      <div className="ti-auth-card">
        <h2 className="ti-auth-title">Welcome back</h2>
        <p className="ti-auth-sub">
          Apna mobile number daalo — ek baar sign-up ke baad login seedha hoga.
        </p>
        <form onSubmit={login}>
          <div className="ti-auth-field">
            <label htmlFor="ti-login-phone">Mobile number</label>
            <input id="ti-login-phone" type="tel" inputMode="tel" autoComplete="tel"
              placeholder="10-digit mobile" value={phone}
              onChange={(e) => setPhone(e.target.value)} required />
          </div>
          {err ? <p className="ti-auth-err">{err}</p> : null}
          <button type="submit" className="ti-auth-primary" disabled={busy}>
            {busy ? <Loader2 className="animate-spin" size={18} style={{ verticalAlign: "middle" }} /> : null}
            {busy ? " Logging in…" : "Continue"}
          </button>
        </form>
        <button type="button" className="ti-auth-link" onClick={() => { setMode("signup"); setErr(""); }}>
          New team member? Sign up here
        </button>
      </div>
    </div>
  );
}
